import { themeType, type KitFontId, type ReleaseThemeId } from '../releaseThemes';

import { LYRIC_CARD_FULL_LYRIC_PX, STORY_FORMATS, type StoryFormatId } from './storyFormats';
import { STORY_FRAMES } from './storyFrames';
import { STORY_FIT_FLOOR, storyLayout } from './storyLayout';
import type { KitStory, StoryStyle } from './storyTypes';

/**
 * The fit meter of the lyric line picker (design `studio-details` frame b): does a lyric line fit
 * the hero at full size, does the type get smaller, or is the line too long? The limits come from
 * the font metrics of the release theme (`KIT_FONT_METRICS`), not from a count of characters.
 */

/** A Story, a feed post and a lyric card fit the lyric with the Story layout (`storyLayout`), each in its own format. */
export type LyricFitAssetType = 'story' | 'feedPost' | 'lyricCard';

/** `full`: the full size. `shrinks`: smaller, at or above 70 %. `tooLong`: below 70 %. */
export type LyricFitStateName = 'full' | 'shrinks' | 'tooLong';

export interface LyricFitInput {
  text: string;
  assetTypeId: LyricFitAssetType;
  releaseThemeId: ReleaseThemeId;
  fontOverride: KitFontId | null;
  /** The Story style. Absent: the cover card. Type only has a larger lyric. */
  storyStyle?: StoryStyle;
}

export interface LyricFitState {
  state: LyricFitStateName;
  /** The fitted size over the full size (1: full size). */
  sizeShare: number;
}

/** A fitted size at or above this share of the full size counts as the full size (rounding of the fit). */
const FULL_SHARE = 0.99;

/** The Story that carries the lyric: the other texts are typical, so the free lyric height is typical too. */
function sampleStory(text: string, format: StoryFormatId): KitStory {
  const feed = format !== 'story';
  return {
    format,
    moment: 'lyric',
    title: 'Forests',
    artist: 'LaziiBee',
    kicker: null,
    hero: text,
    nowWord: null,
    dayLabel: null,
    when: null,
    cta: 'Listen now',
    link: 'laziibee.music24.link/forests',
    linkNote: feed ? 'Link in bio' : null,
    withLogo: false,
  };
}

function stateOf(sizeShare: number): LyricFitStateName {
  if (sizeShare >= FULL_SHARE) {
    return 'full';
  }
  return sizeShare >= STORY_FIT_FLOOR ? 'shrinks' : 'tooLong';
}

function storySizeShare(input: LyricFitInput): number {
  const type = themeType({ themeId: input.releaseThemeId, fontOverride: input.fontOverride });
  const frame = STORY_FRAMES[input.releaseThemeId];
  const style = input.storyStyle ?? 'card';
  // The 4:5 feed post is the feed format of the meter: the square is the same stack, scaled.
  const format: StoryFormatId = input.assetTypeId === 'feedPost' ? 'feed45' : 'story';
  const { sizes } = STORY_FORMATS[format];
  const base = style === 'type' ? sizes.lyricType : sizes.lyric;
  const maxSize = base * frame.factors.display * frame.factors.lyric * type.display.scale;
  // The Story layout fits the lyric (`fitLyric`) in the column and the free height of the lyric Story.
  const layout = storyLayout({
    story: sampleStory(input.text, format),
    themeId: input.releaseThemeId,
    style,
    fonts: type.fonts,
    typeStyle: { display: type.display, text: type.text },
  });
  const hero = layout.items.find((item) => item.kind === 'hero');
  if (hero === undefined || !('sizePx' in hero) || maxSize <= 0) {
    return 1;
  }
  return hero.sizePx / maxSize;
}

/**
 * The lyric card (design `asset-redesign/k Lyric card - new`, owner answers 2026-10-08) is the lyric
 * Story fitted to a square (`lyric11`): the lyric takes the free height of the stack and grows up to
 * 216 px; the full size is xl, 108 px, for every theme (owner default 6), and the floor is 70 %.
 */
function lyricCardSizeShare(input: LyricFitInput): number {
  const type = themeType({ themeId: input.releaseThemeId, fontOverride: input.fontOverride });
  const layout = storyLayout({
    story: sampleStory(input.text, 'lyric11'),
    themeId: input.releaseThemeId,
    // A lyric card has the styles Cover ground and Type only; the meter of a Studio pick measures it.
    style: input.storyStyle === 'type' ? 'type' : 'ground',
    fonts: type.fonts,
    typeStyle: { display: type.display, text: type.text },
  });
  const hero = layout.items.find((item) => item.kind === 'hero');
  if (hero === undefined || !('sizePx' in hero)) {
    return 1;
  }
  return hero.sizePx / (LYRIC_CARD_FULL_LYRIC_PX * type.display.scale);
}

/** The fit of a lyric line (one line, or two lines with a line break) in the hero of the asset. Pure. */
export function lyricFitState(input: LyricFitInput): LyricFitState {
  if (input.text.trim() === '') {
    return { state: 'full', sizeShare: 1 };
  }
  const raw = input.assetTypeId === 'lyricCard' ? lyricCardSizeShare(input) : storySizeShare(input);
  const sizeShare = Math.min(1, Math.round(raw * 1000) / 1000);
  return { state: stateOf(sizeShare), sizeShare };
}

/** The words of the sample lyric that measures the limits: common English words of mixed length. */
const SAMPLE_WORDS =
  'we run through the night and the lights keep burning bright under the open sky while the city sleeps and the bass rolls on and on until the morning comes'.split(
    ' '
  );

export interface LyricFitLimits {
  /** The most characters of a typical line at full size. */
  fullChars: number;
  /** The most characters of a typical line before it is too long. */
  floorChars: number;
}

/**
 * The limits of the meter: the length of a typical lyric that still fits at full size, and the
 * length that still fits at 70 %. Two lines put the break in the middle of the words. Pure.
 */
export function lyricFitLimits(input: Omit<LyricFitInput, 'text'> & { twoLines: boolean }): LyricFitLimits {
  let fullChars = 0;
  let floorChars = 0;
  for (let count = 1; count <= SAMPLE_WORDS.length; count += 1) {
    const words = SAMPLE_WORDS.slice(0, count);
    const half = Math.ceil(count / 2);
    const text =
      input.twoLines && count > 1
        ? `${words.slice(0, half).join(' ')}\n${words.slice(half).join(' ')}`
        : words.join(' ');
    const chars = words.join(' ').length;
    const { state } = lyricFitState({ ...input, text });
    if (state === 'full') {
      fullChars = chars;
    }
    if (state !== 'tooLong') {
      floorChars = chars;
    } else {
      break;
    }
  }
  return { fullChars, floorChars };
}
